import { useRef } from "react";
import { cjsDefault } from "../../lib/cjs-default";
import reactFadeIn from "react-fade-in";
import { useTranslation } from "react-i18next";
import ContentHeader from "../../components/app/headers/ContentHeader";
import ArticlePublicationCard from "../../components/cards/ArticlePublicationCard";
import InfographicCard from "../../components/cards/InfographicCard";

import VideoCard from "../../components/cards/VideoCard";
import ForYouBlenderEmpty from "../../components/forYou/ForYouBlenderEmpty";
import ScrollableLayout from "../../components/layout/ScrollableLayout";
import DownloadAppModal from "../../components/modals/DownloadAppModal";
import SkeletonPublicationCard from "../../components/skeleton/SkeletonPublicationCard";
import { useEffectOnce } from "../../domains/app/hooks/useEffectOnce";
import { usePageScroll } from "../../domains/app/hooks/usePageScroll";
import { useGetForYouRecommendedContentQuery } from "../../domains/forYou/hooks/useGetForYouRecommendedContentQuery";
import { useCheckUserAccessRegistered } from "../../domains/user/hooks/useCheckUserAccessRegistered";
import { AnalyticsItemList } from "../../tools/analytics/analytics.types";
import { gaEventViewItemListMixedContent } from "../../tools/analytics/domains/content.analytics";

const FadeIn = cjsDefault(reactFadeIn);

export default function ForYouPage() {
  useCheckUserAccessRegistered({ memorizePath: true });
  const { t } = useTranslation();
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const { savePageScrollY } = usePageScroll(wrapperRef);
  const cache = useGetForYouRecommendedContentQuery(null, null, null);
  const { contentList, contentListIds, isLoading } = cache;

  useEffectOnce(() => {
    if (contentList.length > 0) {
      gaEventViewItemListMixedContent({
        content: contentList,
        listName: AnalyticsItemList.FORYOU,
      });
    }
  }, [contentListIds]);

  function handleScroll() {
    savePageScrollY(wrapperRef.current?.scrollTop ?? 0);
  }

  function handleScrollEnd() {
    if (cache.canFetchNextPage) cache.fetchNextPage();
  }

  return (
    <ScrollableLayout
      scrollRef={wrapperRef}
      background="var(--color-ds-white)"
      header={
        <ContentHeader backTo="/discovery" title={t("For You")} transparent />
      }
      onScroll={handleScroll}
      onScrollEnd={handleScrollEnd}
    >
      {isLoading ? (
        <div className="flex-1 px-3 py-4 flex flex-col gap-3">
          {Array.from({ length: 5 }).map((_, idx) => (
            <SkeletonPublicationCard showImage key={idx} showDescription />
          ))}
        </div>
      ) : contentList.length === 0 ? (
        <ForYouBlenderEmpty />
      ) : (
        <FadeIn>
          <div className="flex-1 px-3 py-4 flex flex-col gap-3">
            {contentList.map((content) => {
              switch (content.content_format) {
                case "Article": {
                  return (
                    <ArticlePublicationCard
                      roomId={null}
                      key={content.id}
                      article={content}
                      analyticsListName={AnalyticsItemList.FORYOU}
                      recommendation={content.recommendation}
                      onClick={null}
                      className={null}
                      showImage={true}
                    />
                  );
                }
                case "Nectar": {
                  return null;
                }
                case "Video": {
                  return (
                    <VideoCard
                      roomId={null}
                      key={content.id}
                      video={content}
                      analyticsListName={AnalyticsItemList.FORYOU}
                      recommendation={null}
                      onClick={null}
                      className={null}
                    />
                  );
                }
                case "Infographic": {
                  return (
                    <InfographicCard
                      roomId={null}
                      key={content.id}
                      infographic={content}
                      analyticsListName={AnalyticsItemList.FORYOU}
                      recommendation={null}
                      onClick={null}
                      className={null}
                    />
                  );
                }
                case "Audio": {
                  return null;
                }
                default: {
                  throw new Error(content satisfies never);
                }
              }
            })}
          </div>
        </FadeIn>
      )}

      <DownloadAppModal />
    </ScrollableLayout>
  );
}
